import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import YPResourcBookingObject from '../yesplan_classes/YPResourceBookingObject';
import { printInHourDisplayFormat } from '../libs/dateTimePrinting';
import ListItem from './ListItem/ListItem';
import CollapsibleListItem from './CollapsibleListItem/CollapsibleListItem';
import { Body, Meta, Subhead } from '../styled components/Typography';
import FormatSettingsContext from './FormatSettingsContext';

/** This component can be used to render a Yesplan ResourceBooking object.
 *  All resource bookings, except those for sets, are rendered as
 *  `name - role × number`, at the left side and the booked times aligned right.
 *
 *  Sets are rendered as the name of the set and an expandable list, where the bookings in the set are
 *  rendered as a list of resource bookings.
 *  To render a set initially as expanded or collapsed can be controlled with the `collapsed` property.
 *
 *  Bookings of type `resourcebookinggroup` with only one child are rendered as that only child.
 */

function YPResourceBooking({ booking, collapsed = true, resourceType = null }) {
  const { timeFormatSetting } = useContext(FormatSettingsContext);

  function renderResourceBooking(booking) {
    return (
      <div tw="flex justify-between gap-8 w-full" data-test-type="resourceBooking">
        <Body tw="min-w-0 grow" data-test-type="resourceBookingName" $color="black">
          {booking.printBookingWithRoleString()}
          <Meta
            tw="ml-4"
            as="span"
            data-test-type="resourceBookingQuantity"
            $color="gray dark"
          >
            {booking.printQuantityString()}
          </Meta>
        </Body>
        <Subhead
          tw="shrink-0 mt-2"
          data-test-type="resourceBookingTime"
          $color="gray dark"
        >
          {printInHourDisplayFormat(booking.start, timeFormatSetting)}{' – '}
          {printInHourDisplayFormat(booking.end, timeFormatSetting)}
        </Subhead>
      </div>
    );
  }

  function renderCollapsibleResourceBooking(id, name, children) {
    return (
      <CollapsibleListItem
        tw="mb-4"
        key={id}
        label={name}
        dataAttributes={{
          'data-test-type': 'resourceBooking'
        }}
        initiallyCollapsed={collapsed}
      >
        <div key={`list${id}`} tw="pl-16 pb-8 last:pb-0">
          {sortBy(
            children.filter((booking) =>
              resourceType ? booking.isOfTypeorHasChildOfType(resourceType) : true
            ),
            [(booking) => booking.name.toUpperCase()]
          ).map((childBooking) => renderResourceBookingType(childBooking))}
        </div>
      </CollapsibleListItem>
    );
  }

  function renderResourceBookingType(booking, topLevel) {
    if (booking.isResourceBookingGroup()) {
      if (booking.hasOnlyOneChildBooking())
        return renderResourceBookingType(booking.getOnlyChildBooking(), topLevel);
      return renderResourceBooking(booking);
    }
    if (booking.isForSet()) {
      return renderCollapsibleResourceBooking(booking.id, booking.name, booking.children);
    }
    if (topLevel) return renderResourceBooking(booking);
    return (
      <ListItem tw="mb-4" key={booking.id}>
        {renderResourceBooking(booking)}
      </ListItem>
    );
  }

  return renderResourceBookingType(booking, true);
}

YPResourceBooking.propTypes = {
  booking: PropTypes.instanceOf(YPResourcBookingObject).isRequired,
  collapsed: PropTypes.bool,
  resourceType: PropTypes.oneOf(['Location', 'Human'])
};

export default YPResourceBooking;
